import React, { useState } from 'react';

/**
 * PhoneInput — country-code picker + local number, emits E.164.
 *
 * Web parity with the mobile apps' phone entry. Defaults to Canada/US (+1).
 * onChange(e164) fires with the composed E.164 string (e.g. "+17807075408") or "" if incomplete.
 * Stateless about validation beyond E.164 shape; callers gate submit on isValidE164(value).
 *
 * No external dependencies (no libphonenumber) — a small curated country list covers the
 * supported markets and keeps the web bundle unchanged.
 */
export const COUNTRY_CODES = [
  { iso: 'CA', label: 'Canada', dial: '1' },
  { iso: 'US', label: 'United States', dial: '1' },
  { iso: 'GB', label: 'United Kingdom', dial: '44' },
  { iso: 'NG', label: 'Nigeria', dial: '234' },
  { iso: 'IN', label: 'India', dial: '91' },
  { iso: 'AU', label: 'Australia', dial: '61' },
  { iso: 'DE', label: 'Germany', dial: '49' },
  { iso: 'FR', label: 'France', dial: '33' },
  { iso: 'ES', label: 'Spain', dial: '34' },
  { iso: 'BR', label: 'Brazil', dial: '55' },
  { iso: 'ZA', label: 'South Africa', dial: '27' },
  { iso: 'AE', label: 'United Arab Emirates', dial: '971' },
];

export function isValidE164(value) {
  return typeof value === 'string' && /^\+[1-9]\d{7,14}$/.test(value);
}

/** Compose E.164 from a dial code + local digits. Returns "" if no local digits. */
export function toE164(dial, local) {
  const localDigits = String(local || '').replace(/\D/g, '');
  if (!localDigits) return '';
  return `+${String(dial).replace(/\D/g, '')}${localDigits}`;
}

const PhoneInput = ({ value = '', onChange, id = 'phone', disabled = false, defaultDial = '1' }) => {
  // Hydrate once (lazy init) from an incoming E.164 value (best-effort: longest known dial prefix).
  const _init = (() => {
    if (value && value.startsWith('+')) {
      const digits = value.slice(1);
      const match = [...COUNTRY_CODES]
        .sort((a, b) => b.dial.length - a.dial.length)
        .find((c) => digits.startsWith(c.dial));
      if (match) return { dial: match.dial, local: digits.slice(match.dial.length) };
    }
    return { dial: defaultDial, local: '' };
  })();
  const [dial, setDial] = useState(_init.dial);
  const [local, setLocal] = useState(_init.local);

  const emit = (nextDial, nextLocal) => {
    if (onChange) onChange(toE164(nextDial, nextLocal));
  };

  return (
    <div className="flex space-x-2">
      <select
        aria-label="Country code"
        value={dial}
        disabled={disabled}
        onChange={(e) => { setDial(e.target.value); emit(e.target.value, local); }}
        className="h-10 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.iso} value={c.dial}>{c.iso} +{c.dial}</option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="Phone number"
        value={local}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value.replace(/[^\d\s-]/g, '');
          setLocal(next);
          emit(dial, next);
        }}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
};

export default PhoneInput;
